import { createReducer } from '@reduxjs/toolkit';
import { apiRequestSuccess } from 'services/api/api.actions';
import {
  inboxUpdateCompleted,
  requestToDeleteSingleEmail,
  requestToRemoveAllInboxMessages,
  setInboxErrors,
  userHasReadMessage,
  userHasSeenMessages,
} from 'services/inbox/inbox.action';
import computeTimezoneOffset from 'utils/functions/computeTimezoneOffset';
import convertApiDateTimeToIsoInstant from 'utils/functions/convertApiDateTimeToIsoInstant';
import {
  getAmountOfMessagesEndpoint,
  getMessageListEndpoint,
} from './inbox.config';
import { patchApiInboxMessage } from './inbox.helpers';
import {
  GetAmountOfMessagesResponsePayload,
  GetMessageListResponsePayload,
  InboxMessage,
  ModifyMessageInfo,
} from './inbox.types';

const initialState = {
  summaryInboxInfos: {
    amountOfUnreadMessages: 0,
    hasUserUnseenMessages: false,
  },
  inboxMessages: {} as Record<string, InboxMessage>,
  errors: {} as Record<string, string>,
  isLoading: false,
  optimisticInboxMessagesPatches: {} as Record<string, ModifyMessageInfo>,
};

const inboxReducer = createReducer(initialState, (buider) =>
  buider
    .addCase(userHasSeenMessages, (state) => {
      const inboxMessagesValues = Object.values(state.inboxMessages);

      for (let i = 0, len = inboxMessagesValues.length; i < len; i++) {
        const { idMessage, visionFlag } = inboxMessagesValues[i];

        if (!visionFlag) {
          if (!state.optimisticInboxMessagesPatches[idMessage]) {
            state.optimisticInboxMessagesPatches[idMessage] = {
              idMessage,
            };
          }
          state.optimisticInboxMessagesPatches[idMessage].visionFlag = 1;
        }
      }
    })
    .addCase(userHasReadMessage, (state, { payload: inboxMessage }) => {
      const { idMessage } = inboxMessage;
      const inboxMessageFromApi = state.inboxMessages[idMessage];

      if (!inboxMessageFromApi) {
        return;
      }

      if (inboxMessageFromApi.readFlag < 1) {
        if (!state.optimisticInboxMessagesPatches[idMessage]) {
          state.optimisticInboxMessagesPatches[idMessage] = {
            idMessage,
          };
        }

        const patchedValues: Pick<
          ModifyMessageInfo,
          'readFlag' | 'visionFlag' | 'deleteFlag'
        > = {
          readFlag: 1,
          visionFlag: 1,
        };

        Object.assign(
          state.optimisticInboxMessagesPatches[idMessage],
          patchedValues,
        );
      }
    })
    .addCase(inboxUpdateCompleted, (state) => {
      state.isLoading = false;
    })
    .addCase(
      requestToDeleteSingleEmail,
      (state, { payload: { idMessage } }) => {
        state.isLoading = true;

        if (!state.optimisticInboxMessagesPatches[idMessage]) {
          state.optimisticInboxMessagesPatches[idMessage] = {
            idMessage,
          };
        }

        state.optimisticInboxMessagesPatches[idMessage].deleteFlag = 1;
      },
    )
    .addCase(requestToRemoveAllInboxMessages, (state) => {
      state.isLoading = true;

      const currentPatches = Object.values(
        state.optimisticInboxMessagesPatches,
      );

      for (let i = 0, len = currentPatches.length; i < len; i++) {
        const currentPatch = currentPatches[i];

        currentPatch.deleteFlag = 1;
      }
    })
    .addCase(apiRequestSuccess, (state, { payload }) => {
      if (payload.resourceId === getAmountOfMessagesEndpoint) {
        const { numMessages, flagVision } = payload.response
          .data as GetAmountOfMessagesResponsePayload;

        if (Number.isFinite(numMessages)) {
          state.summaryInboxInfos.amountOfUnreadMessages = numMessages;
          // Based on `https://test-www.sisal.it/areaprivata/sisal/inbox` behaviour
          // if flagVision is `1` numMessages is the amount of unseen messages
          // (green baloon).
          state.summaryInboxInfos.hasUserUnseenMessages =
            !!flagVision && !!numMessages;
        }

        return;
      }

      if (payload.resourceId === getMessageListEndpoint) {
        const { infoMessageList, datetime } = payload.response
          .data as GetMessageListResponsePayload;

        const serverTimestamp = convertApiDateTimeToIsoInstant(datetime);

        const patchApiInboxMessageOptions = {
          nowEpochTime: (serverTimestamp
            ? new Date(serverTimestamp)
            : new Date()
          ).getTime(),
          timezoneOffset:
            computeTimezoneOffset(
              new Date('2020-01-01T15:35:18.000Z'),
              'Europe/Rome',
            ) || 0,
        };

        if (Array.isArray(infoMessageList)) {
          state.inboxMessages = Object.fromEntries(
            infoMessageList.map((message) => [
              message.idMessage,
              patchApiInboxMessage(message, patchApiInboxMessageOptions),
            ]),
          );
        }

        state.optimisticInboxMessagesPatches =
          initialState.optimisticInboxMessagesPatches;
        return;
      }
    })
    .addCase(setInboxErrors, (state, action) => {
      state.errors = action.payload;
    }),
);

export default inboxReducer;
